import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CompanyResponsibleType } from 'enums/CompanyResponsibleType';
import { environment } from 'environments/environment';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { CompanyResponsibleModel } from 'models/generated/Entities.GormCrm.CompanyResponsible';
import { ContactModel } from 'models/generated/Entities.GormCrm.Contact';
import { ContactPhoneModel } from 'models/generated/Entities.GormCrm.ContactPhone';
import { OrderLineModel } from 'models/generated/Entities.GormCrm.OrderLine';
import { TransferModel } from 'models/generated/Entities.GormCrm.Transfer';
import { BillingInfoDtoModel } from 'models/generated/Models.BillingInfoDto';
import { CompanyQueryModel } from 'models/generated/Models.CompanyQuery';
import { CompanySearchResultViewModel } from 'models/generated/Models.CompanySearchResultView';
import { firstValueFrom, Observable } from 'rxjs';
import { LiveDataTracker, TrackingContext } from 'services/data-tracking';
import { PushService } from 'services/push.service';

@Injectable()
export class CompanyService {

	private baseUrl = `${environment.gatewayUrl}/api/companies`;
	private baseGwUrl = `${environment.gatewayUrl}/api/gateway/company`;
	private companyTracker = new LiveDataTracker<CompanyModel, { companyId: string }>(company => company.id!);
	private billingInfoTracker = new LiveDataTracker<BillingInfoDtoModel, { companyId: string }>(billingInfo => billingInfo.companyId!);

	constructor(private readonly http: HttpClient, pushService: PushService) {
		pushService.onCompanyChanges.subscribe(event => {
			this.companyTracker.refreshData(event, event.data.companyId, () => this.getCompany(event.data.companyId));
			this.billingInfoTracker.refreshData(event, event.data.companyId, () => this.getCompanyBillingInfo(event.data.companyId));
		});
	}

	getCompany(id: string, trackingContext?: TrackingContext): Promise<CompanyModel> {
		return firstValueFrom(this.http.get<CompanyModel>(`${this.baseUrl}/${id}`))
			.then(company => this.companyTracker.trackObjectForComponent(trackingContext, company));
	}

	getCompanyBillingInfo(id: string, trackingContext?: TrackingContext): Promise<BillingInfoDtoModel> {
		return firstValueFrom(this.http.get<BillingInfoDtoModel>(`${this.baseUrl}/${id}/billing-info`))
			.then(billingInfo => this.billingInfoTracker.trackObjectForComponent(trackingContext, billingInfo));
	}

	createCompany(company: CompanyModel): Promise<CompanyModel> {
		company.include(c => c.primaryContact)
		company.primaryContact?.include(c => c.contactPhones);
		return firstValueFrom(this.http.post<CompanyModel>(`${this.baseUrl}`, company));
	}

	getOrCreateCompany(company: CompanyModel): Promise<CompanyModel> {
		return firstValueFrom(this.http.post<CompanyModel>(`${this.baseUrl}/get-or-create`, company));
	}

	searchCompany(companySearch: CompanyQueryModel): Promise<CompanySearchResultViewModel[]> {
		return firstValueFrom(this.http.post<CompanySearchResultViewModel[]>(`${this.baseUrl}/search`, companySearch));
	}

	getCompaniesByIds(companyIds: string[]): Promise<CompanyModel[]> {
		return firstValueFrom(this.http.post<CompanyModel[]>(`${this.baseUrl}/get-companies-by-ids`, companyIds));
	}

	getCompanyResponsibles(companyId: string): Promise<CompanyResponsibleModel[]> {
		return firstValueFrom(this.http.get<CompanyResponsibleModel[]>(`${this.baseUrl}/responsibles/${companyId}`));
	}

	updateCompanyResponsible(companyId: string, role: CompanyResponsibleType, userId: string): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/responsibles/${companyId}/${role}/${userId}`, null));
	}

	removeCompanyResponsible(companyId: string, role: CompanyResponsibleType): Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/responsibles/${companyId}/${role}`));
	}

	transferPlans(transfer: TransferModel): Promise<TransferModel> {
		transfer.include(t => t.transferPlans);
		return firstValueFrom(this.http.post<TransferModel>(`${this.baseGwUrl}/transfer-plans`, transfer));
	}

	getContactName(company?: CompanyModel): string | undefined {
		if (company != undefined && company.companyContacts[0])
			return company.companyContacts[0].contact!.firstName;
		return undefined;
	}

	getContactPhone(company?: CompanyModel): string | undefined {
		if (company != undefined && company.companyContacts.length > 0 && company.companyContacts[0].contact!.contactPhones.length > 0)
			return company.companyContacts[0].contact!.contactPhones[0].number;
		return undefined;
	}

	getContactEmail(company?: CompanyModel): string | undefined {
		if (company != undefined && company.companyContacts[0] != undefined)
			return company.companyContacts[0].contact!.emailAddress;
		return undefined;
	}

	verifyCompanyName(company: CompanyModel): boolean {
		if (company.name.trim() === '') return false;
		return true;
	}

	verifyCompanyAddress(company: CompanyModel): boolean {
		if (company.address.trim() === '') return false;
		if (company.zipcode.trim() === '') return false;
		if (company.city.trim() === '') return false;
		return true;
	}

	getNewContact(): ContactModel {
		let contact = new ContactModel();
		contact.contactPhones.push(new ContactPhoneModel());
		contact.contactPhones.push(new ContactPhoneModel());
		contact.contactPhones[0].priority = 0;
		contact.contactPhones[1].priority = 1;
		return contact;
	}

	updateContact(contact: ContactModel, companyId: string, isPrimary: boolean = false): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/contacts`, { contact: contact, companyId: companyId, isPrimary: isPrimary }));
	}

	updateBillingInfo(billingInfo: BillingInfoDtoModel): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/billing-info`, billingInfo));
	}

	deleteContact(contact: ContactModel, companyId: string): Promise<void> {
		contact.deleted = new Date();
		return this.updateContact(contact, companyId);
	}

	getCompanyFromVatNumber(vatNumber: string): Promise<CompanyModel> {
		return firstValueFrom(this.http.post<CompanyModel>(`${this.baseUrl}/get-from-vat?vatNumber=${vatNumber}`, {}));
	}

	getCompanyVirkDataByVatNumber(vatNumber: string): Promise<CompanyModel> {
		return firstValueFrom(this.http.get<CompanyModel>(`${this.baseUrl}/vat/${vatNumber}`));
	}

	updateCompanyByVatNumber(companyId: string, vatNumber: string): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/${companyId}/by-vat-number?vatNumber=${vatNumber}`, {}));
	}

	searchCompaniesByKeyword(query: string): Observable<CompanySearchResultViewModel[]> {
		return this.http.get<CompanySearchResultViewModel[]>(`${this.baseUrl}/search`, { params: { query } });
	}
	searchCompaniesByProductPlanIds(companyIds: string[], productPlanIds: string[]): Observable<CompanySearchResultViewModel[]> {
		return this.http.post<CompanySearchResultViewModel[]>(`${this.baseUrl}/search-by-ids`, { companyIds, productPlanIds });
	}
	
	getCompanyProducts(companyId: string): Promise<OrderLineModel[]> {
		return firstValueFrom(this.http.get<OrderLineModel[]>(`${this.baseUrl}/${companyId}/products`));
	}

	getCompanyProductsByTag(companyId: string, tag: string): Promise<OrderLineModel[]> {
		return this.getCompanyProducts(companyId).then(products => products.filter(p => p.productVariant!.product!.productTags.some(pt => pt.tag.toLowerCase() === tag)));
	}

	isContactValid(contact: ContactModel, isPrimary: boolean): boolean {

		return (contact.firstName !== '' &&
			(contact.emailAddress !== '' || !isPrimary) &&														// Primary contacts must have an e-mail address
			(contact.emailAddress === '' || contact.emailAddress.isValidEmailAddress()) &&						// If e-mail address exists, it must be valid
			(contact.contactPhones.filter(cp => cp.number.isValidPhoneNumber()).length > 0 || !isPrimary) &&	// Primary contacts must have at least one valid phone number
			contact.contactPhones.every(cp => cp.number === '' || cp.number.isValidPhoneNumber())				// Phone numbers which are filled out must be valid
		);

	}
}
